import { NavLink, Outlet } from "react-router-dom";
import { Activity, AlertTriangle, BellRing, ClipboardCheck, Cpu, TrendingDown, FlaskConical, LogOut, Radio, ScrollText, Server, Settings, ShieldBan } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePoll } from "@/components/common";
import { useEffect, useState } from "react";
import { getTz } from "@/lib/api";

const NAV = [
  { to: "/channels", label: "Channels", icon: Radio },
  { to: "/servers", label: "Authorized servers", icon: Server },
  { to: "/lab", label: "Sources lab", icon: FlaskConical },
  { to: "/providers", label: "Providers & limits", icon: ShieldBan },
  { to: "/access-log", label: "Access log", icon: ScrollText },
  { to: "/incidents", label: "Incidents", icon: AlertTriangle },
  { to: "/reliability", label: "Failure digest", icon: TrendingDown },
  { to: "/acceptance", label: "Acceptance tests", icon: ClipboardCheck },
  { to: "/alerts", label: "Alerts", icon: BellRing },
  { to: "/node", label: "Node", icon: Cpu },
  { to: "/settings", label: "Settings", icon: Settings },
];

function Clock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setT(new Date()), 1000); return () => clearInterval(i); }, []);
  return (
    <span data-testid="header-clock" className="font-mono text-xs text-slate-300">
      {t.toLocaleTimeString("en-US", { timeZone: getTz(), hour12: false })} <span className="text-slate-500">{getTz()}</span>
    </span>
  );
}

function NodeStrip() {
  const { data } = usePoll("/admin/node", 5000);
  if (!data) return null;
  return (
    <div className="flex items-center gap-4 font-mono text-[11px] text-slate-400">
      <span data-testid="header-onair">ON AIR <b className="text-emerald-400">{data.channels_on_air}</b></span>
      <span>OFF AIR <b className={data.channels_off_air ? "text-red-400" : "text-slate-300"}>{data.channels_off_air}</b></span>
      <span>CPU <b className="text-slate-200">{data.cpu_percent.toFixed(0)}%</b></span>
      <span>RAM <b className="text-slate-200">{data.ram_percent.toFixed(0)}%</b></span>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex bg-[#070A0F]">
      <aside className="w-60 shrink-0 border-r border-slate-800 bg-[#0A0E17] flex flex-col">
        <div className="h-14 flex items-center gap-2.5 px-4 border-b border-slate-800">
          <div className="h-7 w-7 rounded bg-cyan-500/15 border border-cyan-500/40 grid place-items-center">
            <Activity size={15} className="text-cyan-400" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-semibold text-sm text-slate-100">MediaCore Origin</div>
            <div className="text-[10px] font-mono text-slate-500">MASTER CONTROL</div>
          </div>
        </div>
        <nav className="flex-1 p-2 space-y-0.5">
          {NAV.map(({ to, label, icon: I }) => (
            <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded text-sm transition-colors ${isActive ? "bg-slate-800/80 text-slate-100 border-l-2 border-cyan-400" : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40 border-l-2 border-transparent"}`}>
              <I size={15} />{label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="text-[11px] font-mono text-slate-500 truncate mb-2">{user?.email}</div>
          <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 text-xs text-slate-400 hover:text-red-400 transition-colors">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 sticky top-0 z-40 border-b border-slate-800 bg-[#0B101B]/90 backdrop-blur-md flex items-center justify-between px-6">
          <NodeStrip />
          <Clock />
        </header>
        <main className="flex-1 p-6 mcr-grid-bg">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
